import { useRef, useState, useEffect, useCallback } from "react";
import html2canvas from "html2canvas";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Sparkles, RefreshCw, ImageIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL?.replace("/api", "") || window.location.origin;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const DEFAULT_AI_IMG =
  "https://images.unsplash.com/photo-1760931969401-9bd6ee902798?crop=entropy&cs=srgb&fm=jpg&q=85&w=500";

function getDescBullets(desc = "") {
  if (!desc) return [];
  let parts = [];
  if (desc.includes(". ")) parts = desc.split(". ").map(s => s.trim()).filter(Boolean);
  else if (desc.includes(",")) parts = desc.split(",").map(s => s.trim()).filter(Boolean);
  else parts = [desc];
  return parts.slice(0, 4);
}

// Parse description into learning categories for two-column card layout
function parseLearningTopics(description) {
  if (!description) return [];
  
  // Split by " - " delimiter (most common format for course topics)
  let topics = [];
  if (description.includes(" - ")) {
    topics = description.split(" - ").map(s => s.trim()).filter(Boolean);
  } else if (description.includes("\n")) {
    topics = description.split("\n").map(s => s.trim()).filter(Boolean);
  } else if (description.includes(". ")) {
    topics = description.split(". ").map(s => s.trim()).filter(Boolean);
  } else {
    topics = [description];
  }
  
  // Clean up topics - remove leading dashes or bullets
  topics = topics.map(t => t.replace(/^[-•]\s*/, '').trim()).filter(t => t.length > 0);
  
  if (topics.length === 0) return [];
  
  // Define categories with keywords for smart matching
  const categoryDefs = [
    { icon: "🧠", title: "Foundation", keywords: ["learn", "basics", "foundation", "step by step", "fundamentals", "prompt engineering", "techniques"] },
    { icon: "💼", title: "Business", keywords: ["business", "entrepreneurship", "linkedin", "chatgpt for", "growth"] },
    { icon: "⚙️", title: "Automation", keywords: ["automation", "api", "workflow", "integration", "mcp", "guardrails"] },
    { icon: "🚀", title: "Advanced AI", keywords: ["rag", "agentic", "advanced", "build apps", "ai apps", "studio", "claude", "emergent"] },
  ];
  
  // Smart categorization - assign topics to matching categories
  const categorized = categoryDefs.map(cat => ({
    ...cat,
    items: []
  }));
  
  const usedTopics = new Set();
  
  // First pass: match topics to categories by keywords
  topics.forEach(topic => {
    const topicLower = topic.toLowerCase();
    for (let i = 0; i < categoryDefs.length; i++) {
      if (categoryDefs[i].keywords.some(k => topicLower.includes(k)) && !usedTopics.has(topic)) {
        // Truncate long text smartly - try to end at a word boundary
        let displayText = topic;
        if (topic.length > 55) {
          displayText = topic.substring(0, 52);
          const lastSpace = displayText.lastIndexOf(' ');
          if (lastSpace > 35) {
            displayText = displayText.substring(0, lastSpace) + "...";
          } else {
            displayText = displayText + "...";
          }
        }
        categorized[i].items.push(displayText);
        usedTopics.add(topic);
        break;
      }
    }
  });
  
  // Second pass: distribute remaining unmatched topics
  const unmatchedTopics = topics.filter(t => !usedTopics.has(t));
  unmatchedTopics.forEach((topic, idx) => {
    // Find category with fewest items
    const minItems = Math.min(...categorized.map(c => c.items.length));
    const targetCat = categorized.find(c => c.items.length === minItems && c.items.length < 3);
    if (targetCat) {
      // Truncate long text smartly
      let displayText = topic;
      if (topic.length > 55) {
        displayText = topic.substring(0, 52);
        const lastSpace = displayText.lastIndexOf(' ');
        if (lastSpace > 35) {
          displayText = displayText.substring(0, lastSpace) + "...";
        } else {
          displayText = displayText + "...";
        }
      }
      targetCat.items.push(displayText);
    }
  });
  
  // Filter out empty categories and limit to 4
  return categorized.filter(cat => cat.items.length > 0).slice(0, 4);
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", {
      day: "numeric", month: "long", year: "numeric"
    });
  } catch { return dateStr; }
}

function formatTime(timeStr) {
  if (!timeStr) return "";
  try {
    const [h, m] = timeStr.split(":");
    const hours = parseInt(h);
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayH = hours % 12 || 12;
    return `${displayH}:${m} ${ampm}`;
  } catch { return timeStr; }
}

export default function PosterModal({ training: initialTraining, onClose }) {
  const posterRef = useRef(null);
  const [training, setTraining] = useState(initialTraining);
  const [downloading, setDownloading] = useState(false);
  const [imgGenerating, setImgGenerating] = useState(false);
  const [imgError, setImgError] = useState("");
  const [posterImg, setPosterImg] = useState(
    initialTraining.poster_image_data || initialTraining.image_url || null
  );

  const registerUrl = `${FRONTEND_URL}/trainings/${training.id}/register`;
  const seatsLeft = training.seats - (training.seats_filled || 0);
  const isFull = seatsLeft <= 0;
  const bullets = getDescBullets(training.description);
  const learningTopics = parseLearningTopics(training.description);
  
  // Offer calculations
  const hasOffer = training.offer_enabled && training.offer_percentage && training.offer_end_date;
  const offerEndDate = hasOffer ? new Date(training.offer_end_date) : null;
  const isOfferValid = hasOffer && offerEndDate && offerEndDate >= new Date();
  const offerPrice = isOfferValid ? Math.round(training.price * (1 - training.offer_percentage / 100)) : training.price;

  const generateImage = useCallback(async () => {
    setImgGenerating(true);
    setImgError("");
    try {
      const res = await axios.post(
        `${API}/admin/trainings/${training.id}/generate-image`,
        {},
        authHeader()
      );
      const dataUrl = res.data.image_data_url;
      setPosterImg(dataUrl);
      setTraining(prev => ({ ...prev, poster_image_data: dataUrl }));
    } catch (err) {
      const msg = err.response?.data?.detail || "Image generation failed. Please try again.";
      setImgError(msg);
    } finally {
      setImgGenerating(false);
    }
  }, [training.id]);

  // Auto-generate if no existing image
  useEffect(() => {
    if (!posterImg) {
      generateImage();
    }
  }, []); // eslint-disable-line

  const downloadPoster = async () => {
    setDownloading(true);
    try {
      const el = posterRef.current;
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#080820",
        logging: false,
        imageTimeout: 15000,
      });
      const link = document.createElement("a");
      link.download = `${training.title.replace(/\s+/g, "_")}_poster.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      console.error("Download error:", e);
    } finally {
      setDownloading(false);
    }
  };

  const displayImg = posterImg || DEFAULT_AI_IMG;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl bg-zinc-900 border border-zinc-800 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <DialogTitle className="font-outfit text-white flex items-center gap-2">
              <span className="text-[#00F0FF]">Training Poster</span>
              <span className="text-zinc-500 text-sm font-normal font-dm-sans">– AI-generated image</span>
            </DialogTitle>
            <button
              data-testid="regenerate-image-btn"
              onClick={generateImage}
              disabled={imgGenerating}
              title="Regenerate AI image"
              className="flex items-center gap-1.5 text-xs font-dm-sans border border-zinc-700 text-zinc-400 hover:border-[#00F0FF] hover:text-[#00F0FF] px-3 py-1.5 transition-colors disabled:opacity-50"
            >
              {imgGenerating ? (
                <><div className="w-3 h-3 border border-zinc-400 border-t-transparent rounded-full animate-spin" /> Generating...</>
              ) : (
                <><RefreshCw size={12} /> Regenerate Image</>
              )}
            </button>
          </div>
        </DialogHeader>

        {/* AI Image Generation Status */}
        {(imgGenerating || imgError) && (
          <div className={`mx-6 mt-3 px-4 py-3 text-sm font-dm-sans flex items-center gap-2 ${
            imgError ? "bg-red-500/10 border border-red-500/30 text-red-400"
              : "bg-[#00F0FF]/10 border border-[#00F0FF]/20 text-[#00F0FF]"
          }`}>
            {imgGenerating ? (
              <>
                <Sparkles size={15} className="animate-pulse" />
                Generating a unique AI image for "{training.title}"... This may take 15-30 seconds.
              </>
            ) : (
              <>
                <ImageIcon size={15} />
                {imgError} Using default image.
              </>
            )}
          </div>
        )}

        {/* Poster Preview */}
        <div className="overflow-y-auto max-h-[65vh] flex justify-center bg-zinc-950 py-5 px-4">
          <a
            href={registerUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", cursor: "pointer", display: "block" }}
            title="Click to open registration page"
          >
            <div
              ref={posterRef}
              data-testid="poster-preview"
              style={{
                width: "600px",
                minWidth: "600px",
                backgroundColor: "#080820",
                background: "linear-gradient(135deg, #080820 0%, #0d0d35 60%, #050515 100%)",
                fontFamily: "Arial, Helvetica, sans-serif",
                overflow: "hidden",
                position: "relative",
                boxSizing: "border-box",
              }}
            >
              {/* Top accent line */}
              <div style={{ height: "5px", background: "linear-gradient(90deg, #00F0FF 0%, #7000FF 100%)", width: "100%" }} />

              {/* Body: two columns */}
              <div style={{ display: "flex", alignItems: "stretch" }}>

                {/* LEFT COLUMN */}
                <div style={{
                  flex: "0 0 58%", padding: "28px 24px 24px 28px",
                  display: "flex", flexDirection: "column", boxSizing: "border-box",
                }}>
                  {/* Price Drop Tag - Only show when offer is active */}
                  {isOfferValid && (
                    <div style={{
                      background: "linear-gradient(90deg, #ef4444 0%, #f97316 50%, #eab308 100%)",
                      padding: "8px 16px",
                      marginBottom: "16px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "10px",
                      borderRadius: "4px",
                      alignSelf: "flex-start",
                      boxShadow: "0 4px 15px rgba(239, 68, 68, 0.4)"
                    }}>
                      <span style={{ fontSize: "18px" }}>🔥</span>
                      <span style={{ 
                        color: "#FFFFFF", 
                        fontWeight: "900", 
                        fontSize: "13px", 
                        letterSpacing: "1px",
                        textTransform: "uppercase"
                      }}>
                        PRICE DROP
                      </span>
                      <span style={{ 
                        color: "#FFFFFF", 
                        opacity: 0.9,
                        fontSize: "11px"
                      }}>|</span>
                      <span style={{ 
                        color: "#FFFFFF", 
                        fontWeight: "800", 
                        fontSize: "14px",
                        letterSpacing: "0.5px"
                      }}>
                        SAVE {training.offer_percentage}%
                      </span>
                    </div>
                  )}

                  {/* Brand */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
                    <span style={{ color: "#FFF", fontWeight: "700", fontSize: "14px" }}>
                      Shanso<span style={{ color: "#00F0FF" }}>AITraining</span>
                    </span>
                  </div>

                  {/* LEARN + arrows */}
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                    <span style={{ color: "#FFF", fontSize: "17px", fontWeight: "600", letterSpacing: "1px" }}>LEARN</span>
                    <span style={{ color: "#00F0FF", fontSize: "16px" }}>&#9658;&#9658;&#9658;</span>
                  </div>

                  {/* Course Title */}
                  <div style={{
                    color: "#FFFFFF",
                    fontSize: training.title.length > 22 ? "24px" : "30px",
                    fontWeight: "900",
                    lineHeight: "1.15",
                    marginBottom: "14px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    wordBreak: "break-word"
                  }}>
                    {training.title}
                  </div>

                  {/* Badge */}
                  <div style={{ marginBottom: "14px" }}>
                    <span style={{
                      background: "linear-gradient(90deg, #7000FF, #00F0FF)",
                      color: "#FFF", fontWeight: "700", fontSize: "11px",
                      padding: "5px 14px", borderRadius: "20px",
                      display: "inline-block", letterSpacing: "0.5px"
                    }}>AI Training Workshop</span>
                  </div>

                  {/* Two-Column Learning Cards */}
                  <div style={{ marginBottom: "18px" }}>
                    <div style={{ 
                      color: "#00F0FF", 
                      fontSize: "10px", 
                      fontWeight: "700", 
                      letterSpacing: "1px", 
                      marginBottom: "10px",
                      textTransform: "uppercase"
                    }}>
                      WHAT YOU'LL LEARN
                    </div>
                    <div style={{ 
                      display: "grid", 
                      gridTemplateColumns: "1fr 1fr", 
                      gap: "8px" 
                    }}>
                      {learningTopics.length > 0 ? learningTopics.map((cat, i) => (
                        <div key={i} style={{
                          background: "rgba(0,240,255,0.05)",
                          border: "1px solid rgba(0,240,255,0.15)",
                          borderRadius: "6px",
                          padding: "10px",
                        }}>
                          <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "6px", 
                            marginBottom: "6px" 
                          }}>
                            <span style={{ fontSize: "14px" }}>{cat.icon}</span>
                            <span style={{ 
                              color: "#00F0FF", 
                              fontSize: "11px", 
                              fontWeight: "700" 
                            }}>{cat.title}</span>
                          </div>
                          {cat.items.map((item, j) => (
                            <div key={j} style={{ 
                              color: "#C0C0D8", 
                              fontSize: "10px", 
                              lineHeight: "1.4",
                              marginBottom: "2px"
                            }}>
                              {item}
                            </div>
                          ))}
                        </div>
                      )) : (
                        <>
                          <div style={{
                            background: "rgba(0,240,255,0.05)",
                            border: "1px solid rgba(0,240,255,0.15)",
                            borderRadius: "6px",
                            padding: "10px",
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                              <span style={{ fontSize: "14px" }}>🧠</span>
                              <span style={{ color: "#00F0FF", fontSize: "11px", fontWeight: "700" }}>Foundation</span>
                            </div>
                            <div style={{ color: "#C0C0D8", fontSize: "10px" }}>Learn from basics</div>
                          </div>
                          <div style={{
                            background: "rgba(0,240,255,0.05)",
                            border: "1px solid rgba(0,240,255,0.15)",
                            borderRadius: "6px",
                            padding: "10px",
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                              <span style={{ fontSize: "14px" }}>🚀</span>
                              <span style={{ color: "#00F0FF", fontSize: "11px", fontWeight: "700" }}>Build Apps</span>
                            </div>
                            <div style={{ color: "#C0C0D8", fontSize: "10px" }}>Real AI projects</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Class Starts */}
                  <div style={{ marginBottom: "8px" }}>
                    <span style={{
                      background: "rgba(0,240,255,0.15)", border: "1px solid rgba(0,240,255,0.3)",
                      color: "#00F0FF", fontSize: "10px", fontWeight: "700",
                      padding: "4px 10px", borderRadius: "4px", letterSpacing: "1px", display: "inline-block"
                    }}>CLASS STARTS</span>
                  </div>

                  {/* Date + Time + Duration */}
                  <div style={{ color: "#FFFFFF", fontSize: "18px", fontWeight: "700", marginBottom: "4px" }}>
                    {formatDate(training.date)}
                  </div>
                  <div style={{ color: "#A0A0C0", fontSize: "13px", fontWeight: "600", marginBottom: "12px" }}>
                    {formatTime(training.time)} &nbsp;·&nbsp; {training.duration}
                  </div>

                  {/* Instructor */}
                  <div style={{
                    color: "#C0C0D8", fontSize: "12px", marginBottom: "16px",
                    display: "flex", alignItems: "center", gap: "6px"
                  }}>
                    <span style={{ color: "#00F0FF" }}>&#128104;</span>
                    <span>Instructor: <strong style={{ color: "#FFF" }}>{training.instructor}</strong></span>
                  </div>

                  {/* REGISTER NOW button */}
                  <div>
                    <div style={{
                      background: "linear-gradient(90deg, #7000FF 0%, #00F0FF 100%)",
                      color: "#FFFFFF", fontWeight: "900", fontSize: "14px",
                      padding: "12px 20px", borderRadius: "6px",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      letterSpacing: "0.5px"
                    }}>
                      <span>REGISTER NOW</span>
                      {isOfferValid ? (
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ textDecoration: "line-through", opacity: 0.6, fontSize: "13px" }}>
                            &#8377;{Number(training.price).toLocaleString("en-IN")}
                          </span>
                          <span style={{ fontSize: "17px" }}>&#8377;{offerPrice.toLocaleString("en-IN")}</span>
                        </span>
                      ) : (
                        <span style={{ fontSize: "17px" }}>&#8377;{Number(training.price).toLocaleString("en-IN")}</span>
                      )}
                    </div>
                    {/* Offer end date - prominent text below button */}
                    {isOfferValid && (
                      <div style={{
                        background: "rgba(239, 68, 68, 0.15)",
                        border: "1px solid rgba(239, 68, 68, 0.4)",
                        borderRadius: "6px",
                        padding: "10px 16px",
                        marginTop: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px"
                      }}>
                        <span style={{ fontSize: "18px" }}>⏰</span>
                        <span style={{
                          color: "#ef4444",
                          fontSize: "14px",
                          fontWeight: "800",
                          letterSpacing: "0.5px"
                        }}>
                          OFFER ENDS: {offerEndDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN – AI-generated image */}
                <div style={{ flex: "0 0 42%", position: "relative", overflow: "hidden", minHeight: "580px" }}>
                  {imgGenerating ? (
                    <div style={{
                      width: "100%", height: "100%", minHeight: "580px",
                      background: "linear-gradient(135deg, #0d0d35, #050515)",
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center", gap: "16px"
                    }}>
                      <div style={{
                        width: "48px", height: "48px",
                        border: "3px solid rgba(0,240,255,0.2)",
                        borderTop: "3px solid #00F0FF",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite"
                      }} />
                      <span style={{ color: "#00F0FF", fontSize: "12px", fontWeight: "600", textAlign: "center", padding: "0 16px" }}>
                        Generating AI image...
                      </span>
                      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </div>
                  ) : (
                    <>
                      <img
                        src={displayImg}
                        alt="AI Training"
                        crossOrigin="anonymous"
                        style={{
                          width: "100%", height: "100%",
                          objectFit: "cover", objectPosition: "center top",
                          display: "block",
                        }}
                      />
                      <div style={{
                        position: "absolute", inset: 0,
                        background: "linear-gradient(to right, #080820 0%, rgba(8,8,32,0.2) 35%, transparent 100%)",
                        pointerEvents: "none"
                      }} />
                      <div style={{
                        position: "absolute", inset: 0,
                        background: "radial-gradient(ellipse at center, rgba(112,0,255,0.2) 0%, transparent 70%)",
                        pointerEvents: "none"
                      }} />
                      <div style={{
                        position: "absolute", top: "16px", right: "10px",
                        background: isFull ? "#27272A" : "#00F0FF",
                        color: isFull ? "#A1A1AA" : "#000",
                        fontWeight: "900", fontSize: "10px",
                        padding: "6px 12px", letterSpacing: "1.5px"
                      }}>
                        {isFull ? "FULLY BOOKED" : "ENROLLING NOW"}
                      </div>
                      {/* AI badge on image */}
                      <div style={{
                        position: "absolute", bottom: "12px", right: "10px",
                        background: "rgba(0,240,255,0.15)",
                        border: "1px solid rgba(0,240,255,0.3)",
                        padding: "3px 8px",
                        display: "flex", alignItems: "center", gap: "4px"
                      }}>
                        <span style={{ color: "#00F0FF", fontSize: "9px", fontWeight: "700", letterSpacing: "1px" }}>
                          &#10024; AI GENERATED
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Bottom bar */}
              <div style={{
                background: "rgba(0,0,0,0.4)", borderTop: "1px solid rgba(0,240,255,0.1)",
                padding: "12px 28px", display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                <span style={{ color: "#00F0FF", fontSize: "16px", fontWeight: "800", letterSpacing: "0.5px", textTransform: "uppercase" }}>shansoai.com</span>
              </div>
              <div style={{ height: "3px", background: "linear-gradient(90deg, #7000FF 0%, #00F0FF 100%)", width: "100%" }} />
            </div>
          </a>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 px-6 py-4 border-t border-zinc-800 bg-zinc-900">
          <button
            data-testid="download-poster-btn"
            onClick={downloadPoster}
            disabled={downloading || imgGenerating}
            className="w-full flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-outfit font-black py-3 px-6 hover:bg-white transition-colors disabled:opacity-60"
          >
            {downloading ? (
              <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Generating...</>
            ) : (
              <><Download size={16} /> Download Poster PNG</>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
